import { computed, defineComponent, onMounted, reactive, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { AccountAction, AccountGetter } from '@/store/account/account.module';
import { Account } from '@/store/account/account.model';
import { Role } from '@/store/role/role.model';
import { Assignment } from '@/store/assignment/assignment.model';
import { AssignmentAction, AssignmentGetter } from '@/store/assignment/assignment.module';
import { FiscalYearAction, FiscalYearGetter } from '@/store/fiscalYear/fiscalYear.module';
import { FiscalYear } from '@/store/fiscalYear/fiscalYear.model';
import { CabinetAction, CabinetGetter } from '@/store/cabinet/cabinet.module';
import { Cabinet } from '@/store/cabinet/cabinet.model';
import { CabinetMinisterAction, CabinetMinisterGetter } from '@/store/cabinetMinister/cabinetMinister.module';
import { CabinetMinister } from '@/store/cabinetMinister/cabinetMinister.model';
import VueSelect from 'vue-select';
import LoadingComponent from '@/components/loading/Loading.vue';

export default defineComponent({
  name: 'MinisterFilterComponent',
  components: {
    VueSelect,
    LoadingComponent,
  },
  emits: [
    'change'
  ],
  setup(_props, context) {
    const store = useStore();
    const account = computed<Account>(() => store.getters[AccountGetter.ACCOUNT]);
    const role = computed<Role | undefined>(() => store.getters[AccountGetter.SINGLE_ROLE]);
    const assignment = computed<Assignment | undefined>(() => store.getters[AssignmentGetter.BY_ID](account.value?.id));
    const isOrs = computed<boolean>(() => store.getters[AccountGetter.IS_ROLE]('เจ้าหน้าที่ ส.กกภ.'));
    const assigned = computed(() => isOrs.value || !!assignment.value);
    const fiscalYears = computed<FiscalYear[]>(() => (store.getters[FiscalYearGetter.ALL] as FiscalYear[]).filter((m) => m.status <= 0));
    const cabinets = computed<Cabinet[]>(() => store.getters[CabinetGetter.BY_FISCAL_YEAR]);
    const cabinetId = ref<number | null | undefined>(null);
    const cabinetMinisters = computed<CabinetMinister[]>(() => store.getters[CabinetMinisterGetter.ALL]);
    const isLoading = computed(() => !isOrs.value && (store.getters[AssignmentGetter.STATUS] === 'init' || store.getters[AssignmentGetter.STATUS] === 'loading'));
    const ministers = computed<Array<{ id: number; name: string; }>>(() => isOrs.value
      ? cabinetMinisters.value.map(m => ({ id: m.minister.id, name: m.minister.fullName }))
      : cabinetMinisters.value.filter(m => assignment.value?.assignments.some(x => x.id === m.minister.id))
          .map(m => ({ id: m.minister.id, name: m.minister.fullName })));

    const selected = reactive({
      fiscalYearId: null as (number | null | undefined),
      ministerId: null as (number | null | undefined),
    });

    onMounted(() => {
      store.dispatch(AccountAction.GET)
        .then(() => store.dispatch(FiscalYearAction.FETCH)
          .then(() => {
            selected.fiscalYearId = fiscalYears.value[0].id;
            fiscalYearChange();

            if (role.value?.name === 'ผู้แทน ร.นรม./รมต.นร') {
              store.dispatch(AssignmentAction.FIND, { mode: 'MINISTER', id: account.value?.id });
            }
          }));
    });

    watch(() => [selected.fiscalYearId, selected.ministerId], ([fiscalYearId, ministerId]) => {
      context.emit('change', {
        fiscalYearId: fiscalYearId,
        ministerId: ministerId,
      });
    });

    // A fiscal year with a mid-year cabinet reshuffle has more than one cabinet;
    // let the user pick which one, then only load ministers from that cabinet.
    const fiscalYearChange = () => {
      store.dispatch(CabinetAction.FETCH_BY_FISCAL_YEAR, selected.fiscalYearId)
        .then(() => {
          cabinetId.value = cabinets.value[0]?.id;
          fetchCabinetMinisters();
        });
    };

    const fetchCabinetMinisters = () => {
      if (cabinetId.value) {
        store.dispatch(CabinetMinisterAction.FETCH, cabinetId.value)
          .then(() => selected.ministerId = ministers.value[0]?.id);
      }
    };

    const cabinetChange = () => {
      fetchCabinetMinisters();
    };

    const cabinetLabel = (cabinet: Cabinet) => {
      const start = `พ.ศ. ${cabinet.startDate.getFullYear() + 543}`;
      const end = cabinet.endDate ? `พ.ศ. ${cabinet.endDate.getFullYear() + 543}` : 'ปัจจุบัน';

      return `${cabinet.name} (${start} - ${end})`;
    };

    return {
      role,
      fiscalYears,
      cabinets,
      cabinetId,
      ministers,
      selected,
      assigned,
      isLoading,
      fiscalYearChange,
      cabinetChange,
      cabinetLabel,
    };
  },
});
